import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { auth, MfaRequiredError, setAuthToken, getAuthToken, setRefreshToken } from '../lib/api';

interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: string;
  account_id: string | null;
  avatar_url?: string | null;
}

interface AuthAccount {
  id: string;
  name: string;
  slug: string;
  type: string;
  plan_tier: string;
  display_id?: string | null;
  custom_domain?: string | null;
  custom_domain_cf_id?: string | null;
  custom_domain_status?: 'pending' | 'pending_ssl' | 'active' | 'failed' | null;
}

interface AuthContextType {
  user: AuthUser | null;
  account: AuthAccount | null;
  /** @deprecated Use `account` instead */
  organization: AuthAccount | null;
  loading: boolean;
  /** True when the current session was started via super admin impersonation. */
  isImpersonating: boolean;
  /** The super admin's user id, if impersonating. */
  impersonatorId: string | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (data: { email: string; password: string; name: string; account_name: string }) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  setUser: (user: AuthUser | null) => void;
  setAccount: (account: AuthAccount | null) => void;
  /** Begin impersonating a target user (super admin only). */
  impersonate: (userId: string) => Promise<void>;
  /** Return to the original super admin session. */
  exitImpersonation: () => Promise<void>;
}

const IMPERSONATOR_KEY = 'rankhelm_impersonator_id';

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [account, setAccount] = useState<AuthAccount | null>(null);
  const [loading, setLoading] = useState(true);
  const [impersonatorId, setImpersonatorId] = useState<string | null>(() =>
    sessionStorage.getItem(IMPERSONATOR_KEY),
  );

  const persistImpersonator = (id: string | null) => {
    setImpersonatorId(id);
    if (id) sessionStorage.setItem(IMPERSONATOR_KEY, id);
    else sessionStorage.removeItem(IMPERSONATOR_KEY);
  };

  const refresh = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setUser(null);
      setAccount(null);
      return;
    }
    try {
      const res = await auth.me();
      setUser(res.data.user);
      setAccount(res.data.account);
    } catch (_e) {
      // Token invalid/expired — clear it
      setAuthToken(null);
      setUser(null);
      setAccount(null);
      persistImpersonator(null);
    }
  }, []);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = async (email: string, password: string) => {
    const res = await auth.login({ email, password });
    // If MFA is required, throw so callers can redirect to the MFA challenge UI
    if (res.data.mfa_required && res.data.mfa_token) {
      throw new MfaRequiredError(res.data.mfa_token);
    }
    setAuthToken(res.data.token ?? null);
    setRefreshToken(res.data.refresh_token ?? null);
    setUser(res.data.user ?? null);
    setAccount(res.data.account ?? null);
    persistImpersonator(null);
  };

  const signup = async (data: { email: string; password: string; name: string; account_name: string }) => {
    const res = await auth.signup(data);
    setAuthToken(res.data.token);
    setRefreshToken((res.data as any).refresh_token ?? null);
    setUser(res.data.user);
    setAccount(res.data.account);
    persistImpersonator(null);
  };

  const logout = async () => {
    try {
      await auth.logout();
    } catch (_e) {
      // ignore
    }
    setAuthToken(null);
    setRefreshToken(null);
    setUser(null);
    setAccount(null);
    persistImpersonator(null);
  };

  const impersonate = async (userId: string) => {
    const res = await auth.impersonate(userId);
    setAuthToken(res.data.token);
    setRefreshToken(res.data.refresh_token);
    setUser(res.data.user);
    setAccount(res.data.account);
    persistImpersonator(res.data.impersonator_id);
  };

  const exitImpersonation = async () => {
    const res = await auth.exitImpersonation();
    setAuthToken(res.data.token);
    setRefreshToken(res.data.refresh_token);
    setUser(res.data.user);
    setAccount(res.data.account);
    persistImpersonator(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        account,
        organization: account,
        loading,
        isImpersonating: impersonatorId !== null,
        impersonatorId,
        login,
        signup,
        logout,
        refresh,
        setUser,
        setAccount,
        impersonate,
        exitImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
